import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lock } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function ForceResetPassword() {
  const { refreshUser } = useAuth();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) { toast.error("Senha mínima de 8 caracteres"); return; }
    if (!/[A-Z]/.test(password)) { toast.error("Senha precisa de pelo menos uma letra maiúscula"); return; }
    if (!/[0-9]/.test(password)) { toast.error("Senha precisa de pelo menos um número"); return; }
    if (!/[!@#$%^&*()_+\-=[\]{};:'",.<>?/\\|`~]/.test(password)) { toast.error("Senha precisa de pelo menos um caractere especial"); return; }
    if (password !== confirm) { toast.error("As senhas não coincidem"); return; }

    setSaving(true);
    try {
      await api.post("/auth/force-reset-password", { new_password: password });
      toast.success("Senha redefinida com sucesso!");
      refreshUser();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Erro ao redefinir senha");
    }
    setSaving(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md border-white/10" data-testid="force-reset-password">
        <CardHeader className="text-center">
          <div className="mx-auto w-12 h-12 rounded-xl bg-yellow-600 flex items-center justify-center mb-4">
            <Lock className="w-6 h-6 text-white" />
          </div>
          <CardTitle className="text-xl">Crie sua nova senha</CardTitle>
          <CardDescription>Por segurança, você precisa criar uma nova senha antes de continuar.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Nova senha</Label>
              <Input data-testid="force-reset-password-input" type="password" value={password}
                onChange={(e) => setPassword(e.target.value)} placeholder="Mínimo 8 caracteres"
                className="bg-black/20 border-white/10" />
            </div>
            <div className="space-y-2">
              <Label>Confirmar nova senha</Label>
              <Input data-testid="force-reset-confirm-input" type="password" value={confirm}
                onChange={(e) => setConfirm(e.target.value)} placeholder="Repita a senha"
                className="bg-black/20 border-white/10" />
            </div>
            <div className="text-xs text-muted-foreground space-y-1 p-3 rounded-lg bg-black/20 border border-white/5">
              <p>A senha deve conter:</p>
              <p className={password.length >= 8 ? "text-emerald-400" : ""}>- Mínimo 8 caracteres</p>
              <p className={/[A-Z]/.test(password) ? "text-emerald-400" : ""}>- Letra maiúscula</p>
              <p className={/[0-9]/.test(password) ? "text-emerald-400" : ""}>- Número</p>
              <p className={/[!@#$%^&*()_+\-=[\]{};:'",.<>?/\\|`~]/.test(password) ? "text-emerald-400" : ""}>- Caractere especial</p>
            </div>
            <Button data-testid="force-reset-submit" type="submit" disabled={saving}
              className="w-full bg-yellow-600 hover:bg-yellow-700 text-white font-semibold">
              {saving ? <div className="loading-spinner" /> : "Redefinir Senha"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
